// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// "Which screen was I on?" in words a developer reading the ClickUp ticket will
// recognise, plus which record was open.
//
// These strings are duplicated from components/Sidebar.tsx on purpose. Exporting
// its private `menu` array would be a permanent change to a shared file in
// service of a temporary feature — and this is display text, not a business
// rule, so the golden rule is not engaged. When this folder is deleted the
// duplication goes with it.

/** Longest prefix wins, so /accounts/payments beats /accounts. */
const PAGE_LABELS: Array<[string, string]> = [
  ['/app', 'Home'],
  ['/rfq', 'Inbox'],
  ['/quotations', 'Quote Generator'],
  ['/confirmed-orders', 'Confirmed Orders'],
  ['/vendor-purchase-orders', 'Vendor Purchase Orders'],
  ['/client-invoices', 'Client Invoices'],
  ['/credit-notes', 'Credit Notes'],
  ['/credit-notes/register', 'Credit Note Register'],
  ['/vendor-invoices', 'Vendor Invoices'],
  ['/client-price-lists', 'Client Price Lists'],
  ['/price-list', 'Vendor Price List'],
  ['/master-catalogue', 'Master Catalogue'],
  ['/item-groups', 'Assorted Item Groups'],
  ['/inventory/pantry', 'Pantry Inventory'],
  ['/inventory', 'Ship Chandlery Inventory'],
  ['/accounts/payments', 'Accounts — Payments'],
  ['/accounts/vouchers', 'Accounts — Vouchers'],
  ['/accounts/suppliers', 'Accounts — Supplier'],
  ['/accounts', 'Accounts'],
  ['/reports', 'Reports'],
  ['/users', 'User Management'],
  ['/clients', 'Client List'],
  ['/vendors', 'Vendor List'],
  ['/settings', 'Settings'],
  ['/currency-conversion', 'Currency Conversion'],
];

/**
 * Which kind of record a detail route is showing. Only routes that take an :id
 * appear here — a list page has no record open, and saying otherwise would send
 * a developer looking for one.
 */
const ENTITY_TYPES: Array<[string, string]> = [
  ['/quotations/', 'quotation'],
  ['/confirmed-orders/', 'confirmed_order'],
  ['/client-invoices/', 'client_invoice'],
  ['/credit-notes/', 'credit_note'],
  ['/vendor-purchase-orders/', 'vendor_purchase_order'],
  ['/client-price-lists/', 'client_price_list'],
  ['/accounts/payments/', 'accounts_payable_payment'],
  ['/accounts/', 'accounts_payable'],
];

function longestPrefixMatch(pathname: string, table: Array<[string, string]>): string {
  let best = '';
  let value = '';
  for (const [prefix, label] of table) {
    if ((pathname === prefix || pathname.startsWith(`${prefix}/`) || pathname.startsWith(prefix)) && prefix.length > best.length) {
      best = prefix;
      value = label;
    }
  }
  return value;
}

export function pageLabelFor(pathname: string): string {
  return longestPrefixMatch(pathname, PAGE_LABELS) || pathname || 'USC';
}

/**
 * The record being viewed. Derived from the path rather than from useParams():
 * MainLayout does sit inside each matched <Route element>, so useParams() would
 * return `{id}` — but it cannot say WHICH KIND of record that id belongs to, and
 * "quotation 68f…" is the half of the answer a developer actually needs. One
 * table gives both, and it keeps working if the widget is ever mounted higher.
 */
export function entityFor(pathname: string): { entityType: string; entityId: string } {
  for (const [prefix, type] of ENTITY_TYPES) {
    if (!pathname.startsWith(prefix)) continue;
    const rest = pathname.slice(prefix.length);
    const id = rest.split('/')[0] || '';
    // "/accounts/vouchers" is a page, not a record — a segment that is a known
    // sub-page must not be reported as an id.
    if (!id || ['payments', 'vouchers', 'suppliers', 'register'].includes(id)) continue;
    return { entityType: type, entityId: id };
  }
  return { entityType: '', entityId: '' };
}
